<template>
  <div class="col-xl-6 col-lg-6 col-sm-12">
    <div class="mb-3">
      <label class="form-label"
        >Orientado a:<span class="text-danger">*</span></label
      >

      <select
        class="form-select mb-2"
        v-model="selectedOriented"
        @change="addOriented"
        :disabled="availableOrienteds.length === 0"
      >
        <option value="">Seleccione una orientación</option>
        <option
          v-for="oriented in availableOrienteds"
          :value="oriented.id"
          :key="oriented.id"
        >
          {{ oriented.name }}
        </option>
      </select>
      <div
        v-if="selectedOrienteds.length > 0"
        class="border rounded p-3 bg-light"
      >
        <div class="d-flex flex-column" style="min-height: 60px">
          <div class="d-flex flex-wrap gap-2 flex-grow-1">
            <div
              v-for="oriented in selectedOrienteds"
              :key="oriented.id"
              class="badge d-flex align-items-center py-2 px-3"
              :style="{ backgroundColor: oriented.color }"
            >
              <span class="text-white">{{ oriented.name }}</span>
              <br />
              <button
                type="button"
                class="btn-close btn-close-white ms-1"
                @click="removeOriented(oriented.id)"
                style="font-size: 0.7em"
              ></button>
            </div>
          </div>
          <div class="mt-auto pt-2">
            <button
              type="button"
              class="btn btn-outline-danger btn-sm"
              @click="clearAll"
            >
              <i class="fa fa-trash"></i> Limpiar selección
            </button>
          </div>
        </div>
      </div>

      <small class="text-muted d-block mt-1">
        <template v-if="selectedOrienteds.length === 0">
          Seleccione las orientaciones para el evento
        </template>
        <template v-else>
          <strong>Tipo actual:</strong> {{ getCurrentTypeLabel() }}.
          <br />
          <span class="text-info">
            Solo puede seleccionar orientaciones del mismo tipo ({{
              selectedOrienteds.length
            }}
            seleccionada{{ selectedOrienteds.length > 1 ? "s" : "" }}).
          </span>
        </template>
      </small>

      <input type="hidden" :name="fieldName" :value="orientedJson" />

      <div v-if="showValidationError" class="text-danger mt-1">
        <i class="fa fa-exclamation-circle"></i> Debe seleccionar al menos una
        orientación
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";

export default {
  name: "MultiOrientedSelector",
  props: {
    orienteds: {
      type: Array,
      default: () => [],
    },
    fieldName: {
      type: String,
      default: "oriented_ids",
    },
    modelValue: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "orienteds-changed"],
  setup(props, { emit }) {
    const selectedOriented = ref("");
    const selectedOrienteds = ref([]);
    const showValidationError = ref(false);

    const getOrientedType = (oriented) => {
      if (oriented.ci) return "ci";
      if (oriented.rda) return "rda";
      if (oriented.rude) return "rude";
      return "general";
    };

    const availableOrienteds = computed(() => {
      if (selectedOrienteds.value.length === 0) {
        return props.orienteds.filter((oriented) => oriented.state !== false);
      }

      const currentType = getOrientedType(selectedOrienteds.value[0]);
      return props.orienteds.filter((oriented) => {
        const isNotSelected = !selectedOrienteds.value.find(
          (s) => s.id === oriented.id
        );
        const isSameType = getOrientedType(oriented) === currentType;
        const isActive = oriented.state !== false;

        return isNotSelected && isSameType && isActive;
      });
    });

    const orientedJson = computed(() => {
      if (selectedOrienteds.value.length === 0) return "";

      const orientedData = {
        type: getOrientedType(selectedOrienteds.value[0]),
        count: selectedOrienteds.value.length,
        first_oriented_id: selectedOrienteds.value[0].id,
        orienteds: selectedOrienteds.value.map((oriented) => ({
          id: oriented.id,
          name: oriented.name,
          color: oriented.color,
          ci: oriented.ci || false,
          rda: oriented.rda || false,
          rude: oriented.rude || false,
        })),
      };

      return JSON.stringify(orientedData);
    });

    const addOriented = () => {
      if (!selectedOriented.value) return;

      const oriented = props.orienteds.find(
        (o) => o.id == selectedOriented.value
      );
      if (!oriented) return;

      const isAlreadySelected = selectedOrienteds.value.find(
        (s) => s.id === oriented.id
      );
      if (isAlreadySelected) {
        selectedOriented.value = "";
        return;
      }

      if (selectedOrienteds.value.length > 0) {
        const currentType = getOrientedType(selectedOrienteds.value[0]);
        const newType = getOrientedType(oriented);

        if (currentType !== newType) {
          selectedOriented.value = "";
          return;
        }
      }

      selectedOrienteds.value.push(oriented);
      selectedOriented.value = "";
      showValidationError.value = false;
    };

    const removeOriented = (orientedId) => {
      selectedOrienteds.value = selectedOrienteds.value.filter(
        (o) => o.id !== orientedId
      );
    };

    const clearAll = () => {
      selectedOrienteds.value = [];
      selectedOriented.value = "";
      showValidationError.value = false;
    };

    const getCurrentTypeLabel = () => {
      if (selectedOrienteds.value.length === 0) return "";

      const type = getOrientedType(selectedOrienteds.value[0]);
      const typeLabels = {
        ci: "CI (Documento de Identidad)",
        rda: "RDA (Registro Docente Administrativo)",
        rude: "RUDE (Registro Único de Estudiantes)",
        general: "CI (Documento de Identidad)",
      };

      return typeLabels[type] || "Desconocido";
    };

    const validate = () => {
      const isValid = selectedOrienteds.value.length > 0;
      showValidationError.value = !isValid;
      return isValid;
    };

    const getSelectedData = () => {
      return {
        json: orientedJson.value,
        count: selectedOrienteds.value.length,
        type:
          selectedOrienteds.value.length > 0
            ? getOrientedType(selectedOrienteds.value[0])
            : null,
        first_oriented_id:
          selectedOrienteds.value.length > 0
            ? selectedOrienteds.value[0].id
            : null,
        orienteds: selectedOrienteds.value,
      };
    };

    const loadExistingData = (orientedsJsonString) => {
      if (!orientedsJsonString) return;

      try {
        const data = JSON.parse(orientedsJsonString);
        if (data.orienteds && Array.isArray(data.orienteds)) {
          selectedOrienteds.value = data.orienteds.map((item) => {
            const fullOriented = props.orienteds.find((o) => o.id == item.id);
            return (
              fullOriented || {
                id: item.id,
                name: item.name,
                color: item.color || "#000000",
                ci: item.ci || false,
                rda: item.rda || false,
                rude: item.rude || false,
                state: true,
              }
            );
          });
        }
      } catch (e) {
        console.warn("Error parsing existing oriented data:", e);
      }
    };

    watch(orientedJson, (newValue) => {
      emit("update:modelValue", newValue);
      emit("orienteds-changed", getSelectedData());
    });

    watch(
      selectedOrienteds,
      () => {
        emit("orienteds-changed", getSelectedData());
      },
      { deep: true }
    );

    onMounted(() => {
      if (props.modelValue) {
        loadExistingData(props.modelValue);
      }
    });
    return {
      selectedOriented,
      selectedOrienteds,
      availableOrienteds,
      orientedJson,
      showValidationError,
      addOriented,
      removeOriented,
      clearAll,
      getCurrentTypeLabel,
      validate,
      getSelectedData,
      loadExistingData,
    };
  },
};
</script>
